import type { FormInfo, FormFieldInfo, SelectOption } from '../types/observations';
import { generateSelector } from '../utils/selector-utils';

export class FormAnalyzer {
  /**
   * Get all forms on the page
   */
  analyzeForms(): FormInfo[] {
    const forms = document.querySelectorAll('form');
    return Array.from(forms).map(form => this.analyzeForm(form));
  }

  /**
   * Analyze a single form
   */
  analyzeForm(form: HTMLFormElement): FormInfo {
    const selector = generateSelector(form);
    const fields = this.getFormFields(form);

    return {
      selector,
      action: form.action || undefined,
      method: form.method || undefined,
      fields
    };
  }

  /**
   * Get all form fields
   */
  private getFormFields(form: HTMLFormElement): FormFieldInfo[] {
    const fields: FormFieldInfo[] = [];
    const fieldElements = form.querySelectorAll('input, select, textarea');

    fieldElements.forEach(element => {
      const fieldInfo = this.getFieldInfo(element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement);
      if (fieldInfo) {
        fields.push(fieldInfo);
      }
    });

    return fields;
  }

  /**
   * Get information about a form field
   */
  private getFieldInfo(
    element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  ): FormFieldInfo | null {
    // Skip hidden fields and buttons
    if (element instanceof HTMLInputElement) {
      const type = element.type.toLowerCase();
      if (['hidden', 'submit', 'button', 'reset', 'image'].includes(type)) {
        return null;
      }
    }

    const selector = generateSelector(element);
    const name = element.name || element.id || '';
    const label = this.findFieldLabel(element);

    const fieldInfo: FormFieldInfo = {
      selector,
      name,
      type: this.getFieldType(element),
      label,
      value: this.getFieldValue(element),
      placeholder: this.getPlaceholder(element),
      required: this.isRequired(element),
      // Add validation information
      validationMessage: element.validationMessage || undefined,
      isValid: element.checkValidity(),
      // Add custom error message from aria-describedby or nearby error elements
      errorMessage: this.getCustomErrorMessage(element),
    };

    // Add checkbox/radio state
    if (element instanceof HTMLInputElement &&
        (element.type === 'checkbox' || element.type === 'radio')) {
      fieldInfo.checked = element.checked;
    }

    // Add select options if it's a select element
    if (element instanceof HTMLSelectElement) {
      fieldInfo.options = this.getSelectOptions(element);
    }

    return fieldInfo;
  }

  /**
   * Get field type
   */
  private getFieldType(element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
    if (element instanceof HTMLInputElement) {
      return element.type;
    }
    if (element instanceof HTMLSelectElement) {
      return 'select';
    }
    if (element instanceof HTMLTextAreaElement) {
      return 'textarea';
    }
    return 'text';
  }

  /**
   * Get field value
   */
  private getFieldValue(element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string | undefined {
    if (element instanceof HTMLInputElement) {
      if (element.type === 'checkbox' || element.type === 'radio') {
        return element.checked ? element.value : undefined;
      }
      return element.value || undefined;
    }

    return element.value || undefined;
  }

  /**
   * Get placeholder
   */
  private getPlaceholder(element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string | undefined {
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      return element.placeholder || undefined;
    }
    return undefined;
  }

  /**
   * Check if field is required
   */
  private isRequired(element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): boolean {
    return element.required || element.hasAttribute('aria-required');
  }

  /**
   * Find label for a form field
   */
  private findFieldLabel(element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string | undefined {
    // Check for label element
    if (element.id) {
      const label = document.querySelector(`label[for="${element.id}"]`);
      if (label) {
        return label.textContent?.trim();
      }
    }

    // Check for parent label
    const parentLabel = element.closest('label');
    if (parentLabel) {
      // Get label text excluding the input's value
      const clone = parentLabel.cloneNode(true) as HTMLElement;
      const input = clone.querySelector('input, select, textarea');
      if (input) {
        input.remove();
      }
      return clone.textContent?.trim();
    }

    // Check for aria-label
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) {
      return ariaLabel;
    }

    // Check for aria-labelledby
    const ariaLabelledBy = element.getAttribute('aria-labelledby');
    if (ariaLabelledBy) {
      const labelElement = document.getElementById(ariaLabelledBy);
      if (labelElement) {
        return labelElement.textContent?.trim();
      }
    }

    // Try to find nearby text
    const previousSibling = element.previousElementSibling;
    if (previousSibling && (previousSibling.tagName === 'LABEL' || previousSibling.tagName === 'SPAN')) {
      return previousSibling.textContent?.trim();
    }

    return undefined;
  }

  /**
   * Get select element options
   */
  private getSelectOptions(select: HTMLSelectElement): SelectOption[] {
    const options: SelectOption[] = [];

    for (let i = 0; i < select.options.length; i++) {
      const option = select.options[i];
      options.push({
        value: option.value,
        text: option.text,
        selected: option.selected
      });
    }

    return options;
  }

  /**
   * Get form by selector
   */
  getForm(selector: string): HTMLFormElement | null {
    const element = document.querySelector(selector);
    return element instanceof HTMLFormElement ? element : null;
  }

  /**
   * Get field by selector
   */
  getField(selector: string): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null {
    const element = document.querySelector(selector);
    if (element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLTextAreaElement) {
      return element;
    }
    return null;
  }

  /**
   * Get custom error message from aria-describedby or nearby error elements
   */
  private getCustomErrorMessage(element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string | undefined {
    // Check aria-describedby for error message
    const describedBy = element.getAttribute('aria-describedby');
    if (describedBy) {
      const errorElement = document.getElementById(describedBy);
      if (errorElement && errorElement.textContent) {
        const text = errorElement.textContent.trim();
        // Only return if it looks like an error (common patterns)
        if (text && (
          errorElement.classList.contains('error') ||
          errorElement.classList.contains('invalid') ||
          errorElement.getAttribute('role') === 'alert' ||
          element.getAttribute('aria-invalid') === 'true'
        )) {
          return text;
        }
      }
    }

    // Check for error elements with common patterns near the field
    const parent = element.parentElement;
    if (parent) {
      // Look for error messages in common locations
      const errorSelectors = [
        '.error',
        '.error-message',
        '.invalid-feedback',
        '.field-error',
        '[role="alert"]'
      ];

      for (const selector of errorSelectors) {
        const errorEl = parent.querySelector(selector);
        if (errorEl && errorEl.textContent) {
          const text = errorEl.textContent.trim();
          if (text) {
            return text;
          }
        }
      }
    }

    return undefined;
  }
}
